import jsPDF from 'jspdf';

class RedesignPDFExport {

  exportRedesignToPDF(redesignData, fileName = 'PowerPoint_Redesign.pdf') {
    const pdf = new jsPDF();
    let yPosition = 20;
    const pageHeight = pdf.internal.pageSize.height;
    const pageWidth = pdf.internal.pageSize.width;
    const margin = 20;
    const lineHeight = 7;

    // Helper function to add new page if needed
    const checkPageBreak = (requiredSpace) => {
      if (yPosition + requiredSpace > pageHeight - margin) {
        pdf.addPage();
        yPosition = 20;
      }
    };

    // Cover page
    this._addCoverPage(pdf, redesignData);

    // For each slide
    if (redesignData.slides && redesignData.slides.length > 0) {
      redesignData.slides.forEach((slide, index) => {
        pdf.addPage();
        yPosition = 20;

        // Slide header
        pdf.setFontSize(14);
        pdf.setFont(undefined, 'bold');
        pdf.text(`Slide ${slide.slide_number || index + 1} of ${redesignData.metadata.slide_count}`, margin, yPosition);
        yPosition += 10;

        // Slide title if available
        if (slide.title) {
          pdf.setFontSize(11);
          pdf.setFont(undefined, 'italic');
          const titleLines = pdf.splitTextToSize(slide.title, pageWidth - 2 * margin);
          titleLines.forEach(line => {
            pdf.text(line, margin, yPosition);
            yPosition += 6;
          });
          yPosition += 5;
        }

        // Original vs Redesigned images (side-by-side)
        const imgWidth = (pageWidth - 3 * margin) / 2;
        const imgHeight = imgWidth * (9/16); // 16:9 aspect ratio

        // Labels
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'bold');
        pdf.text('ORIGINAL', margin, yPosition);
        pdf.text('REDESIGNED', margin + imgWidth + margin, yPosition);
        yPosition += 5;

        // Original slide image
        if (slide.thumbnail_url) {
          try {
            pdf.addImage(slide.thumbnail_url, 'PNG', margin, yPosition, imgWidth, imgHeight);
          } catch (e) {
            console.error('Failed to add original image:', e);
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'normal');
            pdf.text('Original Slide', margin + imgWidth/2, yPosition + imgHeight/2, { align: 'center' });
          }
        }

        // Redesigned slide image
        if (slide.nano_banana_preview_url) {
          try {
            pdf.addImage(slide.nano_banana_preview_url, 'PNG', margin + imgWidth + margin, yPosition, imgWidth, imgHeight);
          } catch (e) {
            console.error('Failed to add redesigned image:', e);
            pdf.setFontSize(9);
            pdf.setFont(undefined, 'normal');
            pdf.text('Redesigned Slide', margin + imgWidth + margin + imgWidth/2, yPosition + imgHeight/2, { align: 'center' });
          }
        }

        yPosition += imgHeight + 10;

        checkPageBreak(30);

        // Brief explanation
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.text('What Changed?', margin, yPosition);
        yPosition += 7;

        pdf.setFont(undefined, 'normal');
        pdf.setFontSize(10);
        const explanationLines = pdf.splitTextToSize(
          slide.brief_explanation || 'This redesign improves the visual hierarchy and professional appearance.',
          pageWidth - 2 * margin
        );
        explanationLines.forEach(line => {
          checkPageBreak(lineHeight);
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight;
        });
        yPosition += 10;

        // PowerPoint instructions (if they were generated and cached)
        if (slide.ppt_instructions && slide.ppt_instructions.instructions) {
          checkPageBreak(30);

          pdf.addPage();
          yPosition = 20;

          pdf.setFontSize(14);
          pdf.setFont(undefined, 'bold');
          pdf.text(`PowerPoint Instructions - Slide ${slide.slide_number || index + 1}`, margin, yPosition);
          yPosition += 12;

          pdf.setFont(undefined, 'normal');
          pdf.setFontSize(9);

          slide.ppt_instructions.instructions.forEach((instruction) => {
            checkPageBreak(lineHeight * 4);

            pdf.setFont(undefined, 'bold');
            pdf.text(`${instruction.step_number}. ${instruction.action}`, margin, yPosition);
            yPosition += lineHeight;

            pdf.setFont(undefined, 'italic');
            pdf.text(`   Menu: ${instruction.menu_path}`, margin, yPosition);
            yPosition += lineHeight;

            pdf.setFont(undefined, 'normal');
            const detailLines = pdf.splitTextToSize(`   ${instruction.details}`, pageWidth - 2 * margin - 10);
            detailLines.forEach(line => {
              checkPageBreak(lineHeight);
              pdf.text(line, margin, yPosition);
              yPosition += lineHeight;
            });
            yPosition += 3;
          });

          // Assets for this slide
          if (slide.ppt_instructions.assets) {
            checkPageBreak(30);
            yPosition += 5;

            pdf.setFontSize(12);
            pdf.setFont(undefined, 'bold');
            pdf.text('Assets Needed:', margin, yPosition);
            yPosition += 8;

            const assets = slide.ppt_instructions.assets;
            pdf.setFontSize(9);

            if (assets.colors && assets.colors.length > 0) {
              pdf.setFont(undefined, 'bold');
              pdf.text('Colors:', margin + 5, yPosition);
              yPosition += lineHeight;
              assets.colors.forEach(color => {
                checkPageBreak(lineHeight);
                pdf.setFont(undefined, 'normal');
                pdf.text(`  • ${color.name} (${color.hex}) - ${color.usage}`, margin + 10, yPosition);
                yPosition += lineHeight;
              });
              yPosition += 3;
            }

            if (assets.fonts && assets.fonts.length > 0) {
              checkPageBreak(lineHeight * 2);
              pdf.setFont(undefined, 'bold');
              pdf.text('Fonts:', margin + 5, yPosition);
              yPosition += lineHeight;
              assets.fonts.forEach(font => {
                checkPageBreak(lineHeight);
                pdf.setFont(undefined, 'normal');
                pdf.text(`  • ${font.name} ${font.size}pt - ${font.usage}`, margin + 10, yPosition);
                yPosition += lineHeight;
              });
              yPosition += 3;
            }

            if (assets.images && assets.images.length > 0) {
              checkPageBreak(lineHeight * 3);
              pdf.setFont(undefined, 'bold');
              pdf.text('Images:', margin + 5, yPosition);
              yPosition += lineHeight;
              assets.images.forEach(image => {
                checkPageBreak(lineHeight * 2);
                pdf.setFont(undefined, 'normal');
                pdf.text(`  • ${image.description}`, margin + 10, yPosition);
                yPosition += lineHeight;
                if (image.dimensions && image.placement) {
                  pdf.text(`    ${image.dimensions} - ${image.placement}`, margin + 10, yPosition);
                  yPosition += lineHeight;
                }
              });
            }
          }
        }
      });
    }

    // Add asset appendix if we have any instructions
    const hasInstructions = redesignData.slides?.some(s => s.ppt_instructions?.instructions);
    if (hasInstructions) {
      this._addAssetAppendix(pdf, redesignData);
    }

    // Footer on all pages
    const totalPages = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setFont(undefined, 'italic');
      pdf.text(
        `AI Design Compass - PowerPoint Redesign | Page ${i} of ${totalPages}`,
        margin,
        pageHeight - 10
      );
    }

    // Save the PDF
    pdf.save(fileName);
  }

  _addCoverPage(pdf, redesignData) {
    const pageWidth = pdf.internal.pageSize.width;
    const margin = 20;

    pdf.setFontSize(24);
    pdf.setFont(undefined, 'bold');
    pdf.text('PowerPoint Redesign Report', pageWidth / 2, 60, { align: 'center' });

    pdf.setFontSize(14);
    pdf.setFont(undefined, 'normal');
    const date = new Date(redesignData.metadata?.generated_at || Date.now());
    pdf.text(`Generated on: ${date.toLocaleDateString()}`, pageWidth / 2, 80, { align: 'center' });

    pdf.setFontSize(12);
    pdf.text(`Total Slides: ${redesignData.metadata?.slide_count || redesignData.slides?.length || 0}`, pageWidth / 2, 100, { align: 'center' });
    pdf.text(`AI Model: ${redesignData.metadata?.model_used || 'Gemini 3 Pro'}`, pageWidth / 2, 115, { align: 'center' });

    if (redesignData.metadata?.successful_previews) {
      pdf.text(
        `Successfully Redesigned: ${redesignData.metadata.successful_previews} slides`,
        pageWidth / 2,
        130,
        { align: 'center' }
      );
    }

    // Summary box
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, 150, pageWidth - 2 * margin, 80, 'F');

    pdf.setFontSize(11);
    pdf.setFont(undefined, 'bold');
    pdf.text('What\'s Inside:', margin + 10, 165);

    pdf.setFont(undefined, 'normal');
    pdf.setFontSize(10);
    const summaryText = [
      '• Before & After slide comparisons',
      '• Brief redesign explanations for each slide',
      '• Step-by-step PowerPoint menu instructions',
      '• Complete asset library (colors, fonts, images)',
      '• Actionable design improvements'
    ];

    let y = 180;
    summaryText.forEach(line => {
      pdf.text(line, margin + 10, y);
      y += 10;
    });
  }

  _addAssetAppendix(pdf, redesignData) {
    pdf.addPage();
    const margin = 20;
    const pageWidth = pdf.internal.pageSize.width;
    let yPosition = 20;

    pdf.setFontSize(18);
    pdf.setFont(undefined, 'bold');
    pdf.text('APPENDIX: Complete Asset Library', margin, yPosition);
    yPosition += 15;

    // Collect all unique assets across slides
    const allColors = new Set();
    const allFonts = new Set();
    const allImages = [];

    redesignData.slides?.forEach(slide => {
      if (slide.ppt_instructions?.assets) {
        const assets = slide.ppt_instructions.assets;

        if (assets.colors) {
          assets.colors.forEach(color => {
            allColors.add(JSON.stringify({ name: color.name, hex: color.hex, usage: color.usage }));
          });
        }

        if (assets.fonts) {
          assets.fonts.forEach(font => {
            allFonts.add(JSON.stringify({ name: font.name, size: font.size, usage: font.usage }));
          });
        }

        if (assets.images) {
          allImages.push(...assets.images);
        }
      }
    });

    const checkPageBreak = (space) => {
      if (yPosition + space > pdf.internal.pageSize.height - margin) {
        pdf.addPage();
        yPosition = 20;
      }
    };

    // Render color palette
    if (allColors.size > 0) {
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Color Palette', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      Array.from(allColors).forEach(colorStr => {
        checkPageBreak(7);
        const color = JSON.parse(colorStr);
        pdf.text(`• ${color.name} (${color.hex}) - ${color.usage}`, margin + 5, yPosition);
        yPosition += 7;
      });
      yPosition += 10;
    }

    // Render typography system
    if (allFonts.size > 0) {
      checkPageBreak(20);
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Typography System', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      Array.from(allFonts).forEach(fontStr => {
        checkPageBreak(7);
        const font = JSON.parse(fontStr);
        pdf.text(`• ${font.name} ${font.size}pt - ${font.usage}`, margin + 5, yPosition);
        yPosition += 7;
      });
      yPosition += 10;
    }

    // Render image specifications
    if (allImages.length > 0) {
      checkPageBreak(20);
      pdf.setFontSize(14);
      pdf.setFont(undefined, 'bold');
      pdf.text('Image Specifications', margin, yPosition);
      yPosition += 10;

      pdf.setFontSize(10);
      pdf.setFont(undefined, 'normal');
      // Only show first 10 unique images to avoid overflow
      allImages.slice(0, 10).forEach(image => {
        checkPageBreak(14);
        const lines = pdf.splitTextToSize(
          `• ${image.description} (${image.dimensions || 'N/A'}) - ${image.placement || 'N/A'}`,
          pageWidth - 2 * margin - 10
        );
        lines.forEach(line => {
          pdf.text(line, margin + 5, yPosition);
          yPosition += 7;
        });
      });
    }
  }
}

export default new RedesignPDFExport();
